import React, { useEffect, useState } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { Layout, Menu, Spin, message } from 'antd';
import useScreenType from 'react-screentype-hook';
import {
  HomeOutlined,
  WarningOutlined,
  BarChartOutlined,
  CameraOutlined,
  SelectOutlined,
  UsergroupAddOutlined
} from '@ant-design/icons';

import Dashboard from '../dashboard';
import Dashboard2 from '../dashboard2';
import Header from "../../header/header";
import Alert from "../alert";
import Report from "../reports";
import CameraStatus from "../camerastatus";
import CrowdAnalytics from "../crowdAnalytics";
import DrawRoi from "../drawroi";
import PrivateRoute from '../../../layouts/PrivateRoute';

const { Content, Sider } = Layout;

const SidebarVA = ({ path }) => {
  const [selectedMenu, setSelectedMenu] = useState([]);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [collapsed, setCollapsed] = useState(false);

  const citriot = JSON.parse(localStorage.getItem("citriot"));
  const isDemoUser = citriot?.auth?.user === "hkbk";
  const screenType = useScreenType();
  const navigate = useNavigate();
  
  /* ---------------- MENU ITEM HELPER ---------------- */
  const getItem = (label, key, icon) => ({
    key,
    icon,
    label
  });

  /* ---------------- SIDEBAR WIDTH LOGIC ---------------- */
  const siderWidth = screenType.isMobile
    ? collapsed ? 60 : 150
    : collapsed ? 80 : 220;

  /* ---------------- INIT MENU ---------------- */
  useEffect(() => {

    try {
      setLoading(true);

      // 🔹 MENU BASED ON USER TYPE
      const menuItems = isDemoUser
  ? [
      // ✅ DEMO USER → ONLY Dashboard 2
      getItem('Dashboard', `${path}/Dashboard2`, <HomeOutlined />),
      getItem('Camera Status', `${path}/CameraStatus`, <CameraOutlined />),
      getItem('Draw RoI', `${path}/DrawRoi`, <SelectOutlined  />),
      getItem('Crowd Analytics', `${path}/CrowdAnalytics`, <UsergroupAddOutlined />),
      getItem('Alert', `${path}/Alert`, <WarningOutlined />),
      getItem('Report', `${path}/Report`, <BarChartOutlined />),
    ]
  : [
      // ✅ ALL OTHER USERS → ONLY Dashboard
      getItem('Dashboard', `${path}/Dashboard`, <HomeOutlined />),
      getItem('Camera Status', `${path}/CameraStatus`, <CameraOutlined />),
      getItem('Draw RoI', `${path}/DrawRoi`, <SelectOutlined  />),
      getItem('Crowd Analytics', `${path}/CrowdAnalytics`, <UsergroupAddOutlined />),
      getItem('Alert', `${path}/Alert`, <WarningOutlined />),
      getItem('Report', `${path}/Report`, <BarChartOutlined />),
    ];


      setItems(menuItems);

      const currentPath = window.location.pathname;

      // 🔹 DEFAULT ROUTING
      if (currentPath === '/userhome') {
        const defaultRoute = isDemoUser
          ? `${path}/Dashboard2`
          : `${path}/Dashboard`;


        navigate(defaultRoute, { replace: true });
  setSelectedMenu([defaultRoute]);
} else {
  const allowedKeys = menuItems.map(item => item.key);

  if (allowedKeys.includes(currentPath)) {
    setSelectedMenu([currentPath]);
  } else {
    const fallback = isDemoUser
      ? `${path}/Dashboard2`
      : `${path}/Dashboard`;

    navigate(fallback, { replace: true });
    setSelectedMenu([fallback]);
  }
}

      setLoading(false);
    } catch (err) {
      setLoading(false);
      message.error(err.response?.data?.msg || 'Something went wrong');
    }
  }, [path, navigate, isDemoUser]);

  /* ---------------- RENDER ---------------- */
  if (loading) return <Spin />;

  return (
    <Layout>
      <Header auth={citriot?.auth} />

      <Layout hasSider>
        {/* ---------- SIDEBAR ---------- */}
        <Sider
          collapsible
          collapsed={collapsed}
          onCollapse={setCollapsed}
          breakpoint="lg"
          width={screenType.isMobile ? 150 : 220}
          collapsedWidth={screenType.isMobile ? 60 : 80}
          theme="light"
          style={{
            position: 'fixed',
            top: 64,
            bottom: 0,
            left: 0,
            overflowY: 'auto',
            backgroundColor: '#ffffff',
            borderRight: '1px solid #f0f0f0'
          }}
        >
          <Menu
            mode="inline"
            selectedKeys={selectedMenu}
            items={items}
            onSelect={({ key }) => {
              navigate(key);
              setSelectedMenu([key]);
            }}
            style={{
              borderRight: 'none'
            }}
          />
        </Sider>

        {/* ---------- MAIN CONTENT ---------- */}
        <Content
          style={{
            marginLeft: siderWidth,
            padding: '24px',
            transition: 'margin-left 0.2s',
            minHeight: '100vh',
            background: '#f5f5f5'
          }}
        >
          <Routes>
            {/* Dashboard ONLY for non-demo users */}
            {!isDemoUser && (
              <Route path="Dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
            )}

            {/* Dashboard2 for everyone (demo default) */}
            <Route path="Dashboard2" element={<PrivateRoute><Dashboard2 /></PrivateRoute>} />

            <Route path="Alert" element={<PrivateRoute><Alert /></PrivateRoute>} />
            <Route path="Report" element={<PrivateRoute><Report /></PrivateRoute>} />
            <Route path="CameraStatus" element={<PrivateRoute><CameraStatus /></PrivateRoute>} />
            <Route path="CrowdAnalytics" element={<PrivateRoute><CrowdAnalytics /></PrivateRoute>} />
            <Route path="DrawRoi" element={<PrivateRoute><DrawRoi /></PrivateRoute>} />
          </Routes>
        </Content>
      </Layout>
    </Layout>
  );
};

export default SidebarVA;